import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { differenceInCalendarDays, endOfMonth, format, startOfMonth } from "date-fns";
import { RootState } from "@/redux/store";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
} from "@/components/ui/full-calendar/calendar";
import Combobox from "@/components/ui/common/combobox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";
import {
  useGetMembershipAvailabilityCalendarQuery,
  useGetMembershipAvailabilityOptionsQuery,
  useGetMembershipFacilitySchedulesQuery,
} from "@/services/membershipFacilityScheduleApi";
import {
  comboboxLabel,
  findComboboxValue,
  formatTimeRange12,
  toComboboxOptions,
  toHHMM,
} from "@/utils/availabilityUtils";
import { MembershipAvailabilityCalendarWindow } from "@/types/membership-facility-schedule";
import FacilityAvailabilityForm from "../modal/form";
import OccurrenceSheet from "../modal/occurrence-sheet";

const MAX_RANGE_DAYS = 92; // keep in sync with GET /membership_facility_schedule/calendar
const ALL = "all";

type StatusFilter = "all" | "bookable" | "empty" | "changed" | "removed";

/** How many slot-time chips fit in an availability box before "+N more". */
const MAX_SLOT_CHIPS = 4;

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "All days",
  bookable: "With bookable slots",
  empty: "No bookable slots",
  changed: "Changed days",
  removed: "Removed days",
};

interface Filters {
  businessUnitId: number | null;
  membershipId: number | null;
  facilityId: number | null;
  status: StatusFilter;
}

const EMPTY_FILTERS: Filters = {
  businessUnitId: null,
  membershipId: null,
  facilityId: null,
  status: "all",
};

const STATUS_CHOICES = (Object.keys(STATUS_LABELS) as StatusFilter[]).map((status) => ({
  value: status,
  label: STATUS_LABELS[status],
}));

const toId = (value: string): number | null => (value === ALL || value === "" ? null : Number(value));

/**
 * Calendar of availability rules. Each day of a rule is one box; inside it, a chip per schedule
 * slot a member of that membership can book that day (or "No slots"). Slots are not drawn as
 * separate events. Availability never creates slots, so a rule is shown even when its facility
 * has no schedule yet.
 * Clicking a day of a rule edits that single day (different hours, remove, restore), like one
 * occurrence of a schedule.
 * Membership, facility, business unit and status are chosen in the filter drawer; leaving
 * membership or facility empty means "all".
 */
const FacilityAvailabilityCalendarView = () => {
  const access = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).facility_availability ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const canEdit = access === "full_access" || access === "write";

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [pending, setPending] = useState<Filters>(EMPTY_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMembershipId, setFormMembershipId] = useState<number | null>(null);
  const [selectedOccurrence, setSelectedOccurrence] =
    useState<MembershipAvailabilityCalendarWindow | null>(null);
  const [initialDate] = useState(() => new Date());
  const [range, setRange] = useState(() => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  }));

  // Names for the summary line (not narrowed by business unit).
  const { data: allOptions, refetch: refetchAllOptions } = useGetMembershipAvailabilityOptionsQuery();
  // Lists offered in the drawer, narrowed by the business unit being picked there.
  const { data: drawerOptions } = useGetMembershipAvailabilityOptionsQuery(
    pending.businessUnitId ? { business_unit_id: pending.businessUnitId } : undefined,
    { skip: !isFilterOpen }
  );
  // For staff limited to some business units the API returns only their units.
  const { data: businessUnitsData } = useGetBusinessUnitsQuery(
    { org_id: Number(orgId), limit: 100 },
    { skip: !orgId || !isFilterOpen }
  );
  const businessUnits = Array.isArray(businessUnitsData?.data) ? businessUnitsData.data : [];

  // Every saved window the staff member may see; drives the default and the facility lists.
  const { data: allWindows, refetch: refetchAllWindows } = useGetMembershipFacilitySchedulesQuery();
  const windows = useMemo(() => allWindows || [], [allWindows]);

  /** Facilities that have availability, for one membership or (null) for any membership. */
  const facilitiesWithAvailability = useCallback(
    (membershipId: number | null) => {
      const map = new Map<number, string>();
      windows
        .filter((w) => membershipId === null || w.membership_plan_id === membershipId)
        .forEach((w) => map.set(w.facility_id, w.facility_name || `Facility #${w.facility_id}`));
      return Array.from(map.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((x, y) => x.name.localeCompare(y.name));
    },
    [windows]
  );

  // ── Drawer option lists ────────────────────────────────────────────────
  const membershipOptions = drawerOptions?.memberships ?? [];
  const pendingFacilityOptions = useMemo(() => {
    const withAvailability = facilitiesWithAvailability(pending.membershipId);
    if (!pending.businessUnitId || !drawerOptions) return withAvailability;
    const inBusinessUnit = new Set(drawerOptions.facilities.map((f) => f.id));
    return withAvailability.filter((f) => inBusinessUnit.has(f.id));
  }, [facilitiesWithAvailability, pending.membershipId, pending.businessUnitId, drawerOptions]);

  // Searchable lists; the first entry of each is its "all" choice.
  const businessUnitChoices = useMemo(
    () => [
      { value: ALL, label: "All business units" },
      ...toComboboxOptions(businessUnits.map((bu) => ({ id: bu.id as number, name: bu.name }))),
    ],
    [businessUnits]
  );
  const membershipChoices = useMemo(
    () => [{ value: ALL, label: "All memberships" }, ...toComboboxOptions(membershipOptions)],
    [membershipOptions]
  );
  const facilityChoices = useMemo(
    () => [{ value: ALL, label: "All facilities" }, ...toComboboxOptions(pendingFacilityOptions)],
    [pendingFacilityOptions]
  );

  const openFilters = () => {
    setPending(applied);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setApplied(pending);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    // Back to the default view: all memberships and all facilities.
    setApplied(EMPTY_FILTERS);
    setPending(EMPTY_FILTERS);
    setIsFilterOpen(false);
  };

  const isFilterApplied =
    applied.membershipId !== null ||
    applied.facilityId !== null ||
    applied.businessUnitId !== null ||
    applied.status !== "all";

  // ── Calendar data ──────────────────────────────────────────────────────
  const rangeIsValid = differenceInCalendarDays(range.end, range.start) <= MAX_RANGE_DAYS;

  const { data, isFetching, refetch: refetchCalendar } = useGetMembershipAvailabilityCalendarQuery(
    {
      start_date: format(range.start, "yyyy-MM-dd"),
      end_date: format(range.end, "yyyy-MM-dd"),
      membership_plan_id: applied.membershipId,
      facility_id: applied.facilityId,
      business_unit_id: applied.businessUnitId,
    },
    { skip: !rangeIsValid }
  );

  const handleRefetchAll = useCallback(async () => {
    await Promise.allSettled([
      refetchCalendar?.(),
      refetchAllWindows?.(),
      refetchAllOptions?.(),
    ]);
  }, [refetchCalendar, refetchAllWindows, refetchAllOptions]);

  const ruleOccurrences = useMemo(
    () => (rangeIsValid ? data?.windows || [] : []),
    [rangeIsValid, data]
  );
  const visibleOccurrences = useMemo(
    () =>
      ruleOccurrences.filter((rule) => {
        const bookable = (rule.slots?.length ?? 0) > 0;
        switch (applied.status) {
          case "bookable":
            return !rule.is_cancelled && bookable;
          case "empty":
            return !rule.is_cancelled && !bookable;
          case "changed":
            return Boolean(rule.is_modified) && !rule.is_cancelled;
          case "removed":
            return Boolean(rule.is_cancelled);
          default:
            return true;
        }
      }),
    [ruleOccurrences, applied.status]
  );

  const events: CalendarEvent[] = useMemo(
    () =>
      visibleOccurrences.map((rule) => {
        // A day can be removed or given its own hours without touching the rest of the rule.
        const color = rule.is_cancelled ? "red" : rule.is_modified ? "orange" : "blue";
        const slotTimes = rule.slots ?? [];

        // One chip per bookable slot, so staff can see what a member can book in this box.
        let tags: { label: string; color: string }[];
        if (rule.is_cancelled) {
          tags = [{ label: "Removed", color: "red" }];
        } else if (slotTimes.length === 0) {
          tags = [{ label: "No slots", color: "gray" }];
        } else {
          tags = slotTimes.slice(0, MAX_SLOT_CHIPS).map((slot) => ({
            label: formatTimeRange12(slot.start_time, slot.end_time),
            color: "green",
          }));
          if (slotTimes.length > MAX_SLOT_CHIPS) {
            tags.push({ label: `+${slotTimes.length - MAX_SLOT_CHIPS} more`, color: "gray" });
          }
        }

        return {
          id: `rule-${rule.id}-${rule.date}`,
          title: `${rule.membership_plan_name || `Membership #${rule.membership_plan_id}`} · ${
            rule.facility_name || `Facility #${rule.facility_id}`
          } (${formatTimeRange12(rule.start_time, rule.end_time)})`,
          start: `${rule.date}T${rule.start_time}`,
          // "until midnight" is stored as 00:00; keep the box on its own day
          end: `${rule.date}T${toHHMM(rule.end_time) === "00:00" ? "23:59:59" : rule.end_time}`,
          color,
          description: rule.name || undefined,
          tags,
          originalData: { kind: "rule", occurrence: rule },
        };
      }),
    [visibleOccurrences]
  );

  const activeDays = ruleOccurrences.filter((rule) => !rule.is_cancelled);
  const bookableSlotCount = activeDays.reduce((sum, rule) => sum + (rule.slots?.length ?? 0), 0);
  const daysWithoutSlots = activeDays.filter((rule) => (rule.slots?.length ?? 0) === 0).length;
  const membershipName = applied.membershipId
    ? allOptions?.memberships.find((m) => m.id === applied.membershipId)?.name ||
      `Membership #${applied.membershipId}`
    : "All memberships";
  const facilityName = applied.facilityId
    ? facilitiesWithAvailability(null).find((f) => f.id === applied.facilityId)?.name ||
      `Facility #${applied.facilityId}`
    : "All facilities";

  let hint: string | null = null;
  if (rangeIsValid && !isFetching && ruleOccurrences.length === 0) {
    hint = isFilterApplied
      ? "No availability matches these filters in the visible range. Change the filters or use Create New."
      : "No availability in the visible range. Use Create New to add one.";
  } else if (rangeIsValid && !isFetching && visibleOccurrences.length === 0) {
    hint = "No availability days match the selected status.";
  } else if (rangeIsValid && !isFetching && activeDays.length > 0 && bookableSlotCount === 0) {
    hint =
      "None of these availability days has a schedule slot inside it. Availability does not create slots; it applies once a schedule covers it.";
  }

  const openForm = (membershipId: number | null) => {
    setFormMembershipId(membershipId);
    setIsFormOpen(true);
  };

  return (
    <Card className="p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="text-sm text-foreground" data-testid="filter-summary">
          {membershipName} · {facilityName}
          {applied.status !== "all" ? ` · ${STATUS_LABELS[applied.status]}` : ""}
        </span>
        <Badge className="bg-blue-200 text-blue-900 hover:bg-blue-200">Availability</Badge>
        <Badge className="bg-orange-200 text-orange-900 hover:bg-orange-200">Changed day</Badge>
        <Badge className="bg-red-200 text-red-900 hover:bg-red-200">Removed day</Badge>
        <span className="inline-flex items-center gap-1">
          <span className="rounded bg-green-500 px-1 text-[9px] font-medium leading-4 text-white">
            10:00 - 11:00 AM
          </span>
          = a slot members can book
        </span>
        {rangeIsValid && !isFetching && activeDays.length > 0 && (
          <span data-testid="slot-summary">
            {bookableSlotCount} bookable {bookableSlotCount === 1 ? "slot" : "slots"} in{" "}
            {activeDays.length} availability {activeDays.length === 1 ? "day" : "days"}
            {daysWithoutSlots > 0 ? ` · ${daysWithoutSlots} without slots` : ""}
          </span>
        )}
      </div>

      {hint && (
        <p role="status" className="text-sm text-muted-foreground">
          {hint}
        </p>
      )}

      <FullCalendar
        events={events}
        initialView="month"
        initialDate={initialDate}
        access={access}
        refetch={handleRefetchAll}
        hideEventActions
        allowGridView={false}
        isFilterApplied={isFilterApplied}
        onFilterEvents={openFilters}
        onCreateEvent={() => openForm(applied.membershipId)}
        // Clicking a day of a rule opens that one day: change its hours, remove it or restore it.
        onViewEvent={(event) => {
          if (event.originalData?.kind === "rule") setSelectedOccurrence(event.originalData.occurrence);
        }}
        onDateRangeChange={(start, end) => setRange({ start, end })}
      />

      <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <SheetContent className="!h-screen">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <Separator className="h-[1px] rounded-full my-2" />
          <div className="py-2 space-y-4">
            <Combobox
              label="Business Unit"
              list={businessUnitChoices}
              defaultValue={comboboxLabel(businessUnitChoices, pending.businessUnitId ?? ALL)}
              setFilter={(label: string) =>
                // The membership and facility lists depend on the business unit.
                setPending((prev) => ({
                  ...prev,
                  businessUnitId: toId(findComboboxValue(businessUnitChoices, label) ?? ALL),
                  membershipId: null,
                  facilityId: null,
                }))
              }
            />

            <Combobox
              label="Membership"
              list={membershipChoices}
              defaultValue={comboboxLabel(membershipChoices, pending.membershipId ?? ALL)}
              setFilter={(label: string) =>
                // The facility list depends on the membership.
                setPending((prev) => ({
                  ...prev,
                  membershipId: toId(findComboboxValue(membershipChoices, label) ?? ALL),
                  facilityId: null,
                }))
              }
            />

            <Combobox
              label="Facility"
              list={facilityChoices}
              defaultValue={comboboxLabel(facilityChoices, pending.facilityId ?? ALL)}
              setFilter={(label: string) =>
                setPending((prev) => ({
                  ...prev,
                  facilityId: toId(findComboboxValue(facilityChoices, label) ?? ALL),
                }))
              }
            />

            <Combobox
              label="Status"
              list={STATUS_CHOICES}
              defaultValue={comboboxLabel(STATUS_CHOICES, pending.status)}
              setFilter={(label: string) =>
                setPending((prev) => ({
                  ...prev,
                  status: (findComboboxValue(STATUS_CHOICES, label) ?? "all") as StatusFilter,
                }))
              }
            />

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="border-primary text-black font-semibold w-full"
                onClick={resetFilters}
              >
                Reset
              </Button>
              <Button className="bg-primary text-black font-semibold w-full" onClick={applyFilters}>
                <i className="fa fa-filter mr-2" />
                Apply Filter
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <OccurrenceSheet
        occurrence={selectedOccurrence}
        onClose={() => setSelectedOccurrence(null)}
        onEditRule={openForm}
        onUpdated={handleRefetchAll}
        // only write-capable access may change a day; "read", "no_access" or anything else may not
        readOnly={!canEdit}
      />

      <FacilityAvailabilityForm
        isOpen={isFormOpen}
        setOpen={setIsFormOpen}
        initialMembershipId={formMembershipId}
        onSaved={(savedMembershipId) => {
          // If a different membership is filtered, widen the view so the new rule is visible.
          setApplied((prev) =>
            prev.membershipId && prev.membershipId !== savedMembershipId
              ? { ...prev, membershipId: null, facilityId: null }
              : prev
          );
          handleRefetchAll();
        }}
      />
    </Card>
  );
};

export default FacilityAvailabilityCalendarView;
